import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts"
import { useMemo } from "react"
import {
  cleanColumnName,
  formatCellValue,
  detectColumnType,
  formatDisplayValue,
  type ColumnType,
} from "./dataUtils"

const CHART_COLORS = [
  "#66DECD", // cyan
  "#A57CFB", // purple
  "#5C8BD6", // blue
  "#F4D148", // yellow
  "#EE8434", // orange
  "#DB32B2", // magenta
]

type DataRow = Record<string, string | number>

export interface ChartAnalysis {
  type: "line" | "bar"
  dimensionKey: string
  measureKeys: string[]
}

const DATE_KEYWORDS = [
  "date",
  "day",
  "week",
  "month",
  "quarter",
  "year",
  "time",
  "timestamp",
  "period",
  "created_at",
  "updated_at",
]

function isDateLikeKey(key: string): boolean {
  const lower = key.toLowerCase()
  const localName = lower.split(".").at(-1) ?? lower
  return DATE_KEYWORDS.some(
    s =>
      localName === s ||
      localName.startsWith(`${s}_`) ||
      localName.endsWith(`_${s}`),
  )
}

/** Check if the values in a column look like ISO dates (YYYY-MM-DD or YYYY-MM) */
function hasDateLikeValues(data: DataRow[], key: string): boolean {
  const sample = data.slice(0, 5)
  return sample.every(row => {
    const v = row[key]
    return typeof v === "string" && /^\d{4}-\d{2}(-\d{2})?/.test(v)
  })
}

export function analyzeData(data: DataRow[]): ChartAnalysis | null {
  if (data.length < 2) return null

  const keys = Object.keys(data[0])
  const numericKeys = keys.filter(k =>
    data.every(row => typeof row[k] === "number"),
  )
  const stringKeys = keys.filter(k => !numericKeys.includes(k))

  if (numericKeys.length === 0) return null

  // Find date dimension: first check key names, then fall back to value patterns
  const dateKey =
    keys.find(k => isDateLikeKey(k)) ??
    stringKeys.find(k => hasDateLikeValues(data, k))

  if (dateKey) {
    const measures = numericKeys.filter(k => k !== dateKey)
    if (measures.length > 0) {
      return { type: "line", dimensionKey: dateKey, measureKeys: measures }
    }
  }

  if (stringKeys.length === 1 && data.length <= 20 && numericKeys.length > 0) {
    return {
      type: "bar",
      dimensionKey: stringKeys[0],
      measureKeys: numericKeys,
    }
  }

  return null
}

const tooltipContentStyle = {
  backgroundColor: "#1D2939",
  border: "1px solid #374151",
  borderRadius: 6,
  fontSize: 11,
} as const

function formatDateTick(v: string | number): string {
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(5, 10)
  if (/^\d{4}-\d{2}$/.test(s)) return `${s.slice(5)}/${s.slice(2, 4)}`
  if (/^\d{4}$/.test(s)) return s
  return s.length > 7 ? s.slice(-7) : s
}

interface DataChartProps {
  data: DataRow[]
  analysis: ChartAnalysis
}

export function DataChart({ data, analysis }: DataChartProps) {
  const { type, dimensionKey, measureKeys } = analysis
  const visibleMeasures = measureKeys.slice(0, CHART_COLORS.length)

  const columnTypes = useMemo<Record<string, ColumnType>>(() => {
    if (data.length === 0) return {}
    const first = data[0]
    return Object.fromEntries(
      Object.keys(first).map(k => [k, detectColumnType(k, first[k])]),
    )
  }, [data])

  // Map cleaned display name → original key for tooltip lookup
  const cleanToKey = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        Object.keys(data[0] ?? {}).map(k => [cleanColumnName(k), k]),
      ),
    [data],
  )

  const tooltipFormatter = (value: number, name: string) => {
    const origKey = cleanToKey[name]
    const colType = origKey ? columnTypes[origKey] : "number"
    return formatDisplayValue(value, colType ?? "number")
  }

  const labelFormatter =
    type === "line"
      ? (label: string | number) => {
          const colType = columnTypes[dimensionKey]
          return colType === "date"
            ? formatDisplayValue(label, "date")
            : String(label)
        }
      : (label: string | number) => String(label).replace(/_/g, " ")

  const commonProps = {
    data,
    margin: { top: 4, right: 8, left: 0, bottom: 4 },
  }

  return (
    <div className="h-48 my-2">
      <ResponsiveContainer width="100%" height="100%">
        {type === "line" ? (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey={dimensionKey}
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              tickFormatter={formatDateTick}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              tickFormatter={v => formatCellValue(Number(v))}
              width={48}
            />
            <Tooltip
              formatter={tooltipFormatter}
              labelFormatter={labelFormatter}
              contentStyle={tooltipContentStyle}
            />
            {visibleMeasures.length > 1 && (
              <Legend
                wrapperStyle={{ fontSize: 10 }}
                formatter={cleanColumnName}
              />
            )}
            {visibleMeasures.map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={CHART_COLORS[i]}
                strokeWidth={2}
                dot={data.length <= 15}
                name={cleanColumnName(key)}
              />
            ))}
          </LineChart>
        ) : (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey={dimensionKey}
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              interval={0}
              angle={data.length > 8 ? -30 : 0}
              textAnchor={data.length > 8 ? "end" : "middle"}
              height={data.length > 8 ? 50 : 30}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              tickFormatter={v => formatCellValue(Number(v))}
              width={48}
            />
            <Tooltip
              formatter={tooltipFormatter}
              labelFormatter={labelFormatter}
              contentStyle={tooltipContentStyle}
            />
            {visibleMeasures.length > 1 && (
              <Legend
                wrapperStyle={{ fontSize: 10 }}
                formatter={cleanColumnName}
              />
            )}
            {visibleMeasures.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                fill={CHART_COLORS[i]}
                name={cleanColumnName(key)}
                radius={[2, 2, 0, 0]}
              />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  )
}
